import React, { useState, useEffect, useCallback } from 'react';

/**
 * Memory Match Game Component
 * Match concept pairs to discover relationships between program ideas
 * Self-contained with inline styles, no external dependencies
 */

const MemoryMatchGame = ({
  title = "Discover Connections",
  instructions = "Flip cards to find matching pairs",
  data = {
    pairs: []
  },
  onComplete
}) => {
  // Game state
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [moves, setMoves] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasSkipped, setHasSkipped] = useState(false);

  // Initialize and shuffle cards
  useEffect(() => {
    if (data.pairs && data.pairs.length > 0) {
      const gameCards = [];
      data.pairs.forEach((pair, index) => {
        gameCards.push({
          id: `${index}-a`,
          pairId: index,
          content: pair.cardA,
          type: 'a',
          explanation: pair.explanation
        });
        gameCards.push({
          id: `${index}-b`,
          pairId: index,
          content: pair.cardB,
          type: 'b',
          explanation: pair.explanation
        });
      });
      // Shuffle cards
      const shuffled = [...gameCards].sort(() => Math.random() - 0.5);
      setCards(shuffled);
    }
  }, [data.pairs]);

  // Check if game is complete
  useEffect(() => {
    if (matchedPairs.length === data.pairs.length && data.pairs.length > 0 && !isComplete) {
      setIsComplete(true);
      if (onComplete) {
        setTimeout(onComplete, 1500);
      }
    }
  }, [matchedPairs, data.pairs, isComplete, onComplete]);

  // Handle card flip
  const handleCardClick = useCallback((card) => {
    // Don't flip if:
    // - Card is already matched
    // - Card is already flipped
    // - We're checking a pair
    // - We already have 2 cards flipped
    if (
      matchedPairs.find(pair => pair.pairId === card.pairId) ||
      flippedCards.find(c => c.id === card.id) ||
      isChecking ||
      flippedCards.length >= 2
    ) {
      return;
    }

    const newFlippedCards = [...flippedCards, card];
    setFlippedCards(newFlippedCards);

    // Check for match if we have 2 cards
    if (newFlippedCards.length === 2) {
      setIsChecking(true);
      setMoves(prev => prev + 1);

      const [first, second] = newFlippedCards;
      const isMatch = first.pairId === second.pairId;

      if (isMatch) {
        // Match found!
        setTimeout(() => {
          setMatchedPairs(prev => [...prev, {
            pairId: first.pairId,
            cardA: first.type === 'a' ? first : second,
            cardB: first.type === 'b' ? first : second,
            explanation: first.explanation
          }]);
          setFlippedCards([]);
          setIsChecking(false);
        }, 600);
      } else {
        // No match - flip back
        setTimeout(() => {
          setFlippedCards([]);
          setIsChecking(false);
        }, 1200);
      }
    }
  }, [flippedCards, matchedPairs, isChecking]);

  // Reset game
  const resetGame = () => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setFlippedCards([]);
    setMatchedPairs([]);
    setMoves(0);
    setIsComplete(false);
    setHasSkipped(false);
  };

  // Skip to insights - reveal all pairs
  const handleSkip = () => {
    const allPairs = data.pairs.map((pair, index) => ({
      pairId: index,
      cardA: { content: pair.cardA, type: 'a' },
      cardB: { content: pair.cardB, type: 'b' },
      explanation: pair.explanation
    }));
    setMatchedPairs(allPairs);
    setIsComplete(true);
    setHasSkipped(true);
    if (onComplete) {
      setTimeout(onComplete, 1500);
    }
  };

  // Calculate grid columns based on number of cards
  const getGridColumns = () => {
    const totalCards = cards.length;
    if (totalCards <= 12) return 4;
    if (totalCards <= 16) return 4;
    if (totalCards <= 20) return 5;
    return 6;
  };

  // Check if card is flipped
  const isCardFlipped = (card) => {
    return (
      flippedCards.find(c => c.id === card.id) ||
      matchedPairs.find(pair => pair.pairId === card.pairId)
    );
  };

  // Styles
  const styles = {
    container: {
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      position: 'relative',
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
    },
    title: {
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
      fontSize: 'var(--text-2xl, 28px)',
      fontWeight: '600',
      color: 'var(--navy, #1B2A4A)',
      marginBottom: '10px',
    },
    instructions: {
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      fontSize: 'var(--text-base, 15px)',
      color: 'var(--charcoal, #2D2D2D)',
      marginBottom: '20px',
    },
    stats: {
      display: 'flex',
      justifyContent: 'center',
      gap: '30px',
      marginBottom: '20px',
      fontSize: 'var(--text-sm, 14px)',
      color: 'var(--mid, #B8AFA4)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    gameBoard: {
      display: 'grid',
      gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
      gap: '15px',
      marginBottom: '30px',
      maxWidth: '600px',
      margin: '0 auto 30px',
    },
    cardContainer: {
      position: 'relative',
      aspectRatio: '1',
      cursor: 'pointer',
      transformStyle: 'preserve-3d',
      transition: 'transform 0.6s',
    },
    cardFlipped: {
      transform: 'rotateY(180deg)',
    },
    cardFace: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backfaceVisibility: 'hidden',
      padding: '10px',
      boxSizing: 'border-box',
    },
    cardFront: {
      backgroundColor: 'var(--light, #F2EDE6)',
      border: '2px solid var(--mid, #B8AFA4)',
      transform: 'rotateY(180deg)',
    },
    cardBack: {
      backgroundColor: 'var(--gold, #F7C948)',
      border: '2px solid var(--gold, #F7C948)',
      color: 'white',
      fontSize: '24px',
      fontWeight: 'bold',
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
    },
    cardContent: {
      fontSize: 'var(--text-sm, 14px)',
      fontWeight: '500',
      textAlign: 'center',
      wordWrap: 'break-word',
      color: 'var(--charcoal, #2D2D2D)',
      fontFamily: 'var(--mono, "JetBrains Mono", monospace)',
    },
    cardMatched: {
      backgroundColor: 'var(--teal, #1A8A7D)',
      borderColor: 'var(--teal, #1A8A7D)',
    },
    connectionsZone: {
      marginTop: '40px',
      padding: '20px',
      backgroundColor: '#f9f9f9',
      borderRadius: '12px',
    },
    connectionsTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '20px',
      color: '#333',
    },
    connectionItem: {
      marginBottom: '15px',
      padding: '15px',
      backgroundColor: 'white',
      borderRadius: '8px',
      animation: 'slideIn 0.5s ease-out',
    },
    connectionPair: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginBottom: '10px',
      fontSize: '15px',
      fontWeight: '500',
    },
    connectionArrow: {
      color: '#999',
      fontSize: '18px',
    },
    connectionExplanation: {
      fontSize: '14px',
      color: '#666',
      lineHeight: '1.5',
      paddingTop: '10px',
      borderTop: '1px solid #eee',
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '20px',
    },
    button: {
      padding: '10px 20px',
      borderRadius: 'var(--radius-md, 8px)',
      border: '2px solid var(--light, #F2EDE6)',
      fontSize: 'var(--text-sm, 14px)',
      fontWeight: '500',
      cursor: 'pointer',
      backgroundColor: 'var(--light, #F2EDE6)',
      color: 'var(--charcoal, #2D2D2D)',
      transition: 'all var(--duration-fast, 200ms) ease',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    buttonHover: {
      backgroundColor: 'var(--mid, #B8AFA4)',
      color: 'white',
      borderColor: 'var(--mid, #B8AFA4)',
    },
    skipButton: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      padding: '6px 12px',
      fontSize: 'var(--text-xs, 12px)',
      backgroundColor: 'transparent',
      color: 'var(--mid, #B8AFA4)',
      borderColor: 'var(--mid, #B8AFA4)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: 'var(--radius-md, 8px)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      cursor: 'pointer',
      transition: 'all var(--duration-fast, 200ms) ease',
      zIndex: 10,
    },
    completionMessage: {
      textAlign: 'center',
      padding: '40px',
      fontSize: 'var(--text-2xl, 28px)',
      color: 'var(--teal, #1A8A7D)',
      fontWeight: '600',
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
      animation: 'fadeIn 0.5s ease',
    },
  };

  // Mobile responsive adjustments
  const isMobile = window.innerWidth < 640;
  if (isMobile) {
    styles.gameBoard.gridTemplateColumns = 'repeat(3, 1fr)';
    styles.cardContent.fontSize = '12px';
    styles.cardBack.fontSize = '20px';
    styles.header.paddingTop = '40px'; // Add padding to avoid button overlap
    styles.skipButton.fontSize = '11px';
    styles.skipButton.padding = '5px 10px';
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .card-container:hover {
          transform: scale(1.02);
        }
      `}</style>

      {/* Skip button */}
      {!isComplete && (
        <button
          style={styles.skipButton}
          onClick={handleSkip}
          aria-label="Skip game and view all connections"
        >
          Skip to Insights
        </button>
      )}

      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
        <p style={styles.instructions}>{instructions}</p>

        {/* Stats */}
        <div style={styles.stats}>
          <div style={styles.statItem}>
            <span>Moves:</span>
            <strong>{moves}</strong>
          </div>
          <div style={styles.statItem}>
            <span>Pairs Found:</span>
            <strong>{matchedPairs.length}/{data.pairs.length}</strong>
          </div>
        </div>
      </div>

      {/* Game complete message */}
      {isComplete ? (
        <div style={styles.completionMessage}>
          {hasSkipped
            ? '✓ All connections revealed'
            : `🎉 Excellent! You've matched all pairs in ${moves} moves!`}
        </div>
      ) : (
        <>
          {/* Game board */}
          <div style={styles.gameBoard}>
            {cards.map((card) => {
              const isFlipped = isCardFlipped(card);
              const isMatched = matchedPairs.find(pair => pair.pairId === card.pairId);

              return (
                <div
                  key={card.id}
                  className="card-container"
                  style={{
                    ...styles.cardContainer,
                    ...(isFlipped ? styles.cardFlipped : {}),
                  }}
                  onClick={() => handleCardClick(card)}
                >
                  {/* Front of card (visible when flipped) */}
                  <div
                    style={{
                      ...styles.cardFace,
                      ...styles.cardFront,
                      ...(isMatched ? styles.cardMatched : {}),
                    }}
                  >
                    <div style={styles.cardContent}>
                      {card.content}
                    </div>
                  </div>

                  {/* Back of card (visible when not flipped) */}
                  <div style={{...styles.cardFace, ...styles.cardBack}}>
                    ?
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reset button */}
          <div style={styles.controls}>
            <button
              style={styles.button}
              onClick={resetGame}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
            >
              New Game
            </button>
          </div>
        </>
      )}

      {/* Connections zone - shows matched pairs */}
      {matchedPairs.length > 0 && (
        <div style={styles.connectionsZone}>
          <h3 style={styles.connectionsTitle}>Discovered Connections</h3>
          {matchedPairs.map((pair, index) => {
            // Get the correct order based on type
            const cardA = pair.cardA.type === 'a' ? pair.cardA : pair.cardB;
            const cardB = pair.cardB.type === 'b' ? pair.cardB : pair.cardA;

            return (
              <div key={index} style={styles.connectionItem}>
                <div style={styles.connectionPair}>
                  <span>{cardA.content}</span>
                  <span style={styles.connectionArrow}>↔</span>
                  <span>{cardB.content}</span>
                </div>
                {pair.explanation && (
                  <div style={styles.connectionExplanation}>
                    {pair.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MemoryMatchGame;